/**
 * Bytes: as duas conversões que aparecem em toda tela que lida com arquivo.
 *
 * A partir do TypeScript 5.7, `Uint8Array` carrega no tipo qual buffer ele
 * enxerga, e um `Uint8Array<ArrayBufferLike>` — que é o que sai de leituras
 * genéricas — não serve onde a plataforma pede `ArrayBuffer`. Espalhar `as
 * unknown as` pelo código resolveria o compilador e esconderia o caso em que a
 * conversão é de fato errada: uma visão com deslocamento, em que `.buffer`
 * inclui bytes que não são do arquivo.
 *
 * Então a conversão acontece num lugar só, copiando exatamente a fatia certa.
 * O custo é uma cópia de alguns quilobytes; o ganho é nunca abrir o PDF errado.
 */

/** Cópia dos bytes desta visão, num buffer só dela. */
export function bytesProprios(dados: Uint8Array): ArrayBuffer {
  const copia = new Uint8Array(dados.byteLength);
  copia.set(dados);
  return copia.buffer;
}

/** Blob a partir de bytes, com o tipo declarado. */
export function paraBlob(dados: Uint8Array, tipo: string): Blob {
  return new Blob([bytesProprios(dados)], { type: tipo });
}

/** Endereço temporário para exibir ou baixar. Quem cria, revoga. */
export function urlTemporaria(dados: Uint8Array, tipo: string): string {
  return URL.createObjectURL(paraBlob(dados, tipo));
}

/** SHA-256 em hexadecimal dos bytes. Roda no navegador e no Node 20. */
export async function sha256Bytes(dados: Uint8Array): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytesProprios(dados));
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}
